import { gleich, katalogForm } from "./vorschlaege.js";
import { fuehreZusammen } from "./zusammenfuehren.js";

/* ================================================================
   Änderungshistorie — was hat sich im Katalog getan, das mich angeht?

   Der Server liefert alle Katalogänderungen seit einer Version. Hier
   wird daraus das herausgefiltert, was Objekte im eigenen Kellerbuch
   betrifft, und entschieden, wie damit umzugehen ist:

     eingespielt  Die eigene Fassung entsprach noch dem alten
                  Katalogstand — die Änderung wird still übernommen.
     konflikt     Es gibt eine eigene Abweichung. Dann bleibt die
                  eigene Fassung stehen, und der Nutzer entscheidet —
                  auch nach dem Neuladen noch: Offene Abweichungen
                  liegen im Kellerbuch (`katalogAbweichungen`), weil
                  `katalogGesehen` sofort vorrückt und das Protokoll
                  sie kein zweites Mal liefert.
     abgekoppelt  Nach einer Ablehnung dauerhaft lokal — der Katalog
                  rührt das Objekt nicht mehr an.
     eigeneFassung  Aufnahme des eigenen Objekts, das seit dem
                  Vorschlag weiter bearbeitet wurde — die eigene,
                  neuere Fassung bleibt (siehe unten).

   Bestände, Lagerorte und Preise bleiben in jedem Fall unberührt;
   eingespielt werden ausschließlich gemeinsame Angaben.
   ================================================================ */

const TYP_ZU_LISTE = { wein: "weine", winzer: "winzer", region: "regions" };

async function protokollHolen(seit) {
  const r = await fetch("/api/katalog/protokoll?seit=" + encodeURIComponent(seit != null ? seit : -1));
  if (!r.ok) throw new Error("Das Protokoll konnte nicht geladen werden.");
  return await r.json();
}

/* Führt die gemeinsamen Felder aus dem Katalog in ein eigenes Objekt
   zusammen. Persönliche Angaben am Jahrgang bleiben erhalten; nur
   Jahr und Trinkfenster kommen aus dem Katalog, und neue Jahrgänge
   werden als leere Hüllen ergänzt. */
/* Felder eines Jahrgangs, die der Katalog mitträgt und die nur
   ergänzt, nie überschrieben werden. */
const ANALYSE_FELDER = ["alkohol", "saeure", "restzucker", "ph", "schwefel"];

const leerWert = (v) => v == null || (typeof v === "string" && v.trim() === "");

function analyseErgaenzen(eigenerJg, katalogJg) {
  const raus = {};
  for (const f of ANALYSE_FELDER) {
    if (leerWert(eigenerJg[f]) && !leerWert(katalogJg[f])) raus[f] = katalogJg[f];
  }
  return raus;
}


/* `analyse: "uebernehmen"` setzt die Analysewerte aus der Vorlage,
   statt nur Leeres zu füllen. Nur für das Einspielen aus dem
   Protokoll: Dort ist belegt, dass die eigenen Werte dem alten
   Katalogstand entsprachen (oder der Dreiwegevergleich hat feldweise
   entschieden) — sie sind also keine eigene Messung. Ohne das blieb
   eine korrigierte Säure beim Nutzer alt stehen, und der Abgleich
   schlug dem Admin anschließend vor, die Korrektur zurückzunehmen. */
function verschmelze(typ, eigen, katalogObjekt, { analyse = "ergaenzen" } = {}) {
  const k = katalogForm(typ, katalogObjekt);
  if (typ !== "wein") return { ...eigen, ...k };

  const eigeneJg = new Map((eigen.jahrgaenge || []).map((j) => [j.id, j]));
  const jahrgaenge = (k.jahrgaenge || []).map((kj) => {
    const alt = eigeneJg.get(kj.id);
    if (!alt) {
      return { ...kj, katalogFoto: kj.foto || null, foto: undefined,
        bestaende: {}, lagerort: "", preis: null };
    }
    return {
      ...alt, jahr: kj.jahr, trinkVon: kj.trinkVon, trinkBis: kj.trinkBis,
      /* Analysewerte nur dort einsetzen, wo noch nichts steht.
         Anders als das Trinkfenster sind das oft eigene Messungen
         oder vom Etikett abgelesene Zahlen; sie stillschweigend durch
         eine fremde Fassung zu ersetzen wäre genau die Art
         Änderung, die niemand bemerkt und niemand wollte. */
      ...(analyse === "uebernehmen"
        ? Object.fromEntries(ANALYSE_FELDER.map((f) => [f, kj[f] != null ? kj[f] : (f === "schwefel" ? "" : null)]))
        : analyseErgaenzen(alt, kj)),
      katalogFoto: kj.foto || alt.katalogFoto || null,
    };
  });
  /* Eigene Jahrgänge, die der Katalog nicht kennt, bleiben bestehen. */
  for (const j of eigen.jahrgaenge || []) {
    if (!jahrgaenge.some((x) => x.id === j.id)) jahrgaenge.push(j);
  }
  const rl = Array.isArray(k.rebsortenListe) ? k.rebsortenListe : [];
  const anzeige = rl.length
    ? (rl.every((r) => r.anteil != null)
      ? rl.map((r) => `${r.name} ${Math.round(r.anteil)} %`)
      : rl.map((r) => r.name)).join(" · ")
    : eigen.rebsorten || "";
  return { ...eigen, ...k, rebsorten: anzeige, jahrgaenge };
}

/* Wertet die Protokolleinträge aus. Liefert das ggf. angepasste
   Kellerbuch und je Eintrag den Ausgang zur Anzeige.

   `eigeneVorschlaege` sind die Vorschläge dieses Kontos. Stammt ein
   Eintrag aus einem davon und entspricht die eigene Fassung noch
   genau dem Vorgeschlagenen, wird still nachgezogen — auch wenn der
   Admin korrigiert oder der Katalog daneben weitere Felder geändert
   hat. Sonst stünde der Vorschlagende nach jeder Korrektur vor einer
   Abweichung zu seinem eigenen Vorschlag. */
function verarbeiteProtokoll(db, eintraege, { eigeneVorschlaege = [] } = {}) {
  const ergebnisse = [];
  let neu = db;
  let veraendert = false;
  const eigeneNachId = new Map(eigeneVorschlaege.map((v) => [v.id, v]));

  for (const e of eintraege) {
    /* Wurde im Katalog zusammengeführt, zieht das eigene Kellerbuch
       nach: Quelle geht im Ziel auf, Verweise wandern mit. */
    if (e.art === "zusammenfuehrung" && TYP_ZU_LISTE[e.typ] && e.nachher && e.nachher.ersetztDurch) {
      const feld = TYP_ZU_LISTE[e.typ];
      const liste = neu[feld] || [];
      const quelle = liste.find((o) => o.id === e.objektId);
      if (!quelle) { ergebnisse.push({ eintrag: e, ausgang: "fremd" }); continue; }
      if (quelle.abgekoppelt || quelle.herkunft === "lokal") {
        ergebnisse.push({ eintrag: e, ausgang: "abgekoppelt" });
        continue;
      }
      let zielId = e.nachher.ersetztDurch;
      if (!liste.some((o) => o.id === zielId)) {
        /* Ziel noch nicht vorhanden: aus dem Protokoll übernehmen. */
        if (!e.nachher.ziel) { ergebnisse.push({ eintrag: e, ausgang: "sonstige" }); continue; }
        neu = { ...neu, [feld]: [...liste, { ...e.nachher.ziel, id: zielId, herkunft: "katalog" }] };
      }
      const erg = fuehreZusammen(neu, e.typ, e.objektId, zielId);
      if (erg.bericht) { neu = erg.db; veraendert = true; }
      ergebnisse.push({ eintrag: e, ausgang: erg.bericht ? "zusammengefuehrt" : "sonstige" });
      continue;
    }
    if (e.art === "befuellung" || !TYP_ZU_LISTE[e.typ]) {
      ergebnisse.push({ eintrag: e, ausgang: "sonstige" });
      continue;
    }
    const liste = neu[TYP_ZU_LISTE[e.typ]] || [];
    const eigen = liste.find((o) => o.id === e.objektId);
    if (!eigen) {
      ergebnisse.push({ eintrag: e, ausgang: "fremd" });   /* habe ich nicht */
      continue;
    }
    if (eigen.abgekoppelt || eigen.herkunft === "lokal") {
      ergebnisse.push({ eintrag: e, ausgang: "abgekoppelt" });
      continue;
    }
    if (!e.nachher) {
      ergebnisse.push({ eintrag: e, ausgang: "sonstige" });
      continue;
    }

    /* Entsprach meine Fassung noch dem alten Katalogstand?
       BEIDE Seiten gehen durch `katalogForm`: `vorher` ist das rohe
       Katalogobjekt samt `id` (und bei alten Einträgen ohne die
       Analysewerte). Ohne die Angleichung war jede Katalogänderung
       eine „Abweichung“ — still eingespielt wurde nie etwas. */
    const meins = katalogForm(e.typ, eigen);
    if (gleich(meins, katalogForm(e.typ, e.nachher))) {
      ergebnisse.push({ eintrag: e, ausgang: "schonAktuell" });
      continue;
    }
    const eigenerVorschlag = e.vorschlagId ? eigeneNachId.get(e.vorschlagId) : null;
    const wieVorgeschlagen = !!eigenerVorschlag
      && gleich(meins, katalogForm(e.typ, eigenerVorschlag.nachher));

    /* Eine Aufnahme betrifft praktisch nur den, der sie vorgeschlagen
       hat — nur er führt ein Objekt mit dieser Kennung. Hat er seither
       weiter daran gearbeitet, ist SEINE Fassung die neuere; sie
       bleibt und geht als Änderung an den Admin. Früher galt hier
       „kein vorher, also passt es“, und die Annahme eines älteren
       Vorschlags überschrieb still, was danach getippt wurde. */
    if (e.art === "neu" && !wieVorgeschlagen) {
      ergebnisse.push({ eintrag: e, ausgang: "eigeneFassung" });
      continue;
    }

    const passtZuAlt = !e.vorher || wieVorgeschlagen || gleich(meins, katalogForm(e.typ, e.vorher));
    if (!passtZuAlt) {
      /* Eigene Abweichung — aber an denselben Feldern? Ändert der
         Katalog den Ort und ich habe nur die Beschreibung ergänzt,
         gibt es nichts zu entscheiden: Der Ort kommt, meine
         Beschreibung bleibt. Gefragt wird nur, wo beide Seiten
         dasselbe Feld verschieden geändert haben. */
      /* Auch im Konfliktfall kommt alles Unstrittige sofort an — offen
         bleiben nur die Felder, die beide geändert haben, und dort
         steht bis zur Entscheidung die eigene Angabe. Sonst fehlte
         dem nächsten Protokolleintrag die Grundlage, und aus einer
         strittigen Beschreibung würde auch ein strittiger Ort. */
      const drei = dreiWege(meins, katalogForm(e.typ, e.vorher), katalogForm(e.typ, e.nachher), { meinsGewinnt: true });
      if (!gleich(drei.form, meins)) {
        neu = {
          ...neu,
          [TYP_ZU_LISTE[e.typ]]: liste.map((o) => o.id === e.objektId ? verschmelze(e.typ, o, drei.form, { analyse: "uebernehmen" }) : o),
        };
        veraendert = true;
      }
      ergebnisse.push(drei.konflikte.length > 0
        ? { eintrag: e, ausgang: "konflikt", konfliktFelder: drei.konflikte }
        : { eintrag: e, ausgang: "eingespielt", ergaenzt: true });
      continue;
    }

    neu = {
      ...neu,
      [TYP_ZU_LISTE[e.typ]]: liste.map((o) => o.id === e.objektId
        ? verschmelze(e.typ, o, e.nachher, { analyse: "uebernehmen" }) : o),
    };
    veraendert = true;
    ergebnisse.push({ eintrag: e, ausgang: "eingespielt" });
  }

  return { db: veraendert ? neu : db, ergebnisse };
}

/* ================================================================
   Lücke im Protokoll

   Der Server kappt das Protokoll (speicher/lokal.js, `gekapptBis`).
   Hat ein Gerät länger nicht abgeglichen, fehlen ihm Einträge — und
   damit der Beleg, ob eine Abweichung zwischen eigener Fassung und
   Katalog eine verpasste Katalogänderung ist oder eine eigene. Ohne
   diese Prüfung ging die eigene (alte) Fassung als „Änderung“ an den
   Admin, also als Vorschlag, die Katalogänderung zurückzunehmen.

   Deshalb: Jedes eigene, mit dem Katalog verbundene Objekt, das von
   der Katalogfassung abweicht, wird eine offene Abweichung — der
   Nutzer entscheidet (Katalogfassung übernehmen / Meine behalten).
   Ausgenommen sind Objekte mit offenem eigenen Vorschlag: Dort ist die
   Abweichung belegt die eigene.
   ================================================================ */
const TYP_ZU_KATALOG = { wein: "weine", winzer: "winzer", region: "regionen" };

function lueckenErgebnisse(db, katalog, { eigeneVorschlaege = [], zeit = new Date().toISOString() } = {}) {
  if (!db || !katalog) return [];
  const mitVorschlag = new Set(eigeneVorschlaege.filter((v) => v.status === "offen").map((v) => `${v.typ}:${v.objektId}`));
  const raus = [];
  for (const [typ, feld] of Object.entries(TYP_ZU_LISTE)) {
    const katNachId = new Map((katalog[TYP_ZU_KATALOG[typ]] || []).map((o) => [o.id, o]));
    for (const eigen of db[feld] || []) {
      const k = katNachId.get(eigen.id);
      if (!k || eigen.abgekoppelt || eigen.herkunft === "lokal") continue;
      if (mitVorschlag.has(`${typ}:${eigen.id}`)) continue;
      const meins = katalogForm(typ, eigen);
      const ihrs = katalogForm(typ, k);
      if (gleich(meins, ihrs)) continue;
      const felder = [...new Set([...Object.keys(meins), ...Object.keys(ihrs)])]
        .filter((f) => !gleich(meins[f], ihrs[f]));
      raus.push({
        eintrag: {
          id: `luecke:${typ}:${eigen.id}`, typ, objektId: eigen.id, name: k.name || eigen.name || null,
          zeit, durch: null, vorher: null, nachher: k,
        },
        ausgang: "konflikt",
        konfliktFelder: felder,
      });
    }
  }
  return raus;
}

/* ================================================================
   Dreiwegevergleich auf Feldebene — eigene Fassung, alter und neuer
   Katalogstand, alle drei in `katalogForm`.

   Je Feld: hat nur der Katalog geändert, gilt der Katalog; hat nur
   der Nutzer geändert oder beide gleich, bleibt es; haben beide
   verschieden geändert, ist das ein Konflikt. `meinsGewinnt` legt
   fest, was im Konfliktfall in `form` steht (für „Meine behalten“).

   Jahrgänge zählen je Kennung und Feld. Ein Jahrgang, den der Katalog
   entfernt hat, bleibt — er kann eigene Flaschen tragen; das hält
   `verschmelze` ohnehin so.
   ================================================================ */
function dreiWege(meins, alt, neu, { meinsGewinnt = true } = {}) {
  const konflikte = [];
  const form = { ...meins };
  const wertVon = (o, k) => (o ? o[k] : undefined);
  const entscheide = (pfad, m, a, n, setze) => {
    if (gleich(a, n) || gleich(m, n)) return;          /* Katalog unverändert oder schon gleich */
    if (gleich(m, a)) { setze(n); return; }            /* nur der Katalog hat geändert */
    konflikte.push(pfad);
    if (!meinsGewinnt) setze(n);
  };

  const felder = new Set([...Object.keys(meins || {}), ...Object.keys(alt || {}), ...Object.keys(neu || {})]);
  for (const k of felder) {
    if (k === "jahrgaenge") continue;
    entscheide(k, wertVon(meins, k), wertVon(alt, k), wertVon(neu, k), (w) => { form[k] = w; });
  }

  if (Array.isArray(neu && neu.jahrgaenge)) {
    const altJg = new Map(((alt && alt.jahrgaenge) || []).map((j) => [j.id, j]));
    const meineJg = (meins.jahrgaenge || []).map((j) => ({ ...j }));
    const meineNachId = new Map(meineJg.map((j) => [j.id, j]));
    for (const nj of neu.jahrgaenge) {
      const mj = meineNachId.get(nj.id);
      const aj = altJg.get(nj.id);
      if (!mj) { meineJg.push({ ...nj }); continue; }   /* neuer Katalogjahrgang */
      for (const k of new Set([...Object.keys(nj), ...Object.keys(mj)])) {
        if (k === "id") continue;
        entscheide(`jahrgaenge.${nj.jahr}.${k}`, mj[k], aj ? aj[k] : undefined, nj[k], (w) => { mj[k] = w; });
      }
    }
    form.jahrgaenge = meineJg;
  }
  return { form, konflikte };
}


/* Nachträgliche Übernahme bei einem Konflikt — auf Wunsch des
   Nutzers. Die eigene Abweichung wird dabei verworfen. */
function uebernehmeEintrag(db, eintrag) {
  const feld = TYP_ZU_LISTE[eintrag.typ];
  if (!feld || !eintrag.nachher) return db;
  const liste = db[feld] || [];
  if (!liste.some((o) => o.id === eintrag.objektId)) return db;
  return {
    ...db,
    [feld]: liste.map((o) => o.id === eintrag.objektId
      ? verschmelze(eintrag.typ, o, eintrag.nachher) : o),
  };
}

/* ================================================================
   Offene Abweichungen — bleiben im Kellerbuch, bis der Nutzer
   entscheidet. Je Objekt höchstens eine: Eine spätere Katalogänderung
   ersetzt die frühere, denn übernommen würde ohnehin der neueste
   Stand. Gespeichert wird nur, was zur Entscheidung nötig ist.
   ================================================================ */

const abweichungSchluessel = (typ, objektId) => `${typ}:${objektId}`;

function merkeAbweichungen(db, ergebnisse) {
  const liste = new Map((db.katalogAbweichungen || []).map((a) => [abweichungSchluessel(a.typ, a.objektId), a]));
  let veraendert = false;
  for (const { eintrag: e, ausgang, konfliktFelder } of ergebnisse) {
    if (!TYP_ZU_LISTE[e.typ]) continue;
    const k = abweichungSchluessel(e.typ, e.objektId);
    if (ausgang === "konflikt") {
      liste.set(k, {
        eintragId: e.id, typ: e.typ, objektId: e.objektId, name: e.name || null,
        zeit: e.zeit || null, durch: e.durch || null,
        vorher: e.vorher ? katalogForm(e.typ, e.vorher) : null,
        nachher: katalogForm(e.typ, e.nachher),
        konfliktFelder: konfliktFelder || [],
      });
      veraendert = true;
    } else if (liste.has(k) && ["eingespielt", "schonAktuell", "zusammengefuehrt", "abgekoppelt"].includes(ausgang)) {
      liste.delete(k);
      veraendert = true;
    }
  }
  return veraendert ? { ...db, katalogAbweichungen: [...liste.values()] } : db;
}

/** Was tatsächlich noch offen ist. Erledigt hat sich eine Abweichung,
    wenn das Objekt fehlt, inzwischen lokal geführt wird oder die
    eigene Fassung der Katalogfassung bereits gleicht. */
function offeneAbweichungen(db) {
  return (db && db.katalogAbweichungen || []).filter((a) => {
    const eigen = (db[TYP_ZU_LISTE[a.typ]] || []).find((o) => o.id === a.objektId);
    if (!eigen || eigen.abgekoppelt || eigen.herkunft === "lokal") return false;
    return !gleich(katalogForm(a.typ, eigen), katalogForm(a.typ, a.nachher));
  });
}

const ohneAbweichung = (db, eintragId) => ({
  ...db, katalogAbweichungen: (db.katalogAbweichungen || []).filter((a) => a.eintragId !== eintragId),
});

/** Katalogfassung übernehmen — die eigene Abweichung wird verworfen. */
function abweichungUebernehmen(db, eintragId) {
  const a = (db.katalogAbweichungen || []).find((x) => x.eintragId === eintragId);
  if (!a) return db;
  return ohneAbweichung(uebernehmeEintrag(db, { typ: a.typ, objektId: a.objektId, nachher: a.nachher }), eintragId);
}

/** Eigene Angaben behalten. Strittig waren nur die Felder, die beide
    Seiten geändert haben; alles andere hat `verarbeiteProtokoll`
    schon eingespielt. Der Dreiwegevergleich hier ist die Absicherung
    für Abweichungen, die vor dieser Regel gespeichert wurden — sonst
    schlüge der Abgleich die ganze alte Fassung als Änderung vor und
    nähme dabei Korrekturen zurück, um die es nie ging. Die
    verbleibende eigene Abweichung geht als Änderungsvorschlag an den
    Admin — das ist gewollt: Wer bei seiner Angabe bleibt, hält sie
    für richtig. */
function abweichungBehalten(db, eintragId) {
  const a = (db.katalogAbweichungen || []).find((x) => x.eintragId === eintragId);
  if (!a) return db;
  const feld = TYP_ZU_LISTE[a.typ];
  const eigen = feld && (db[feld] || []).find((o) => o.id === a.objektId);
  if (!eigen || !a.vorher) return ohneAbweichung(db, eintragId);
  const { form } = dreiWege(katalogForm(a.typ, eigen), a.vorher, a.nachher, { meinsGewinnt: true });
  return ohneAbweichung({
    ...db,
    [feld]: db[feld].map((o) => (o.id === a.objektId ? verschmelze(a.typ, o, form, { analyse: "uebernehmen" }) : o)),
  }, eintragId);
}

/** Ein Objekt auf die Katalogfassung zurücksetzen — beim Zurückziehen
    eines Änderungsvorschlags oder nach dessen Ablehnung. Persönliche
    Angaben (Bestände, Lagerorte, Preise) bleiben wie bei jeder
    Übernahme erhalten. */
function aufKatalogfassung(db, typ, objektId, katalogObjekt) {
  if (!katalogObjekt) return db;
  return uebernehmeEintrag(db, { typ, objektId, nachher: katalogObjekt });
}

export {
  abweichungBehalten, abweichungUebernehmen, aufKatalogfassung, dreiWege, lueckenErgebnisse, merkeAbweichungen, offeneAbweichungen,
  protokollHolen, uebernehmeEintrag, verarbeiteProtokoll, verschmelze,
};
